/**
 * Home tab — approved layout of 2026-10-17 (master-context.md → "Home UI
 * specification"):
 *
 *   hero banner → 2 gradient CTAs (Image To Video / Edit Image) →
 *   4 quick-action tiles → sticky, swipeable category tab bar → content.
 *
 * Content:
 * - "All Effects" (default tab): one section per category, in CMS order, each
 *   a horizontal rail with "See all →". Not a mixed grid.
 * - Any category tab: that category only, as a 2-column grid with infinite
 *   loading. "See all →" selects the matching tab (no page navigation).
 *
 * Data: GET /categories drives the tabs (order/visibility are CMS-managed);
 * GET /templates?category= fills rails and grids. Per-category results are
 * cached so switching tabs is instant. The active tab and the scroll position
 * of each tab are remembered for when the user returns to Home.
 */
import { api } from '../api/client.js';
import { icon } from '../components/icon.js';
import { CATEGORY_ICONS } from '../components/categoryIcons.js';
import { escapeHtml } from '../components/placeholder.js';
import {
  bindCardImages,
  templateCardHtml,
  templateCardSkeletonHtml,
} from '../components/templateCard.js';
import { toastApiError } from '../ui/toast.js';
import type { Category, Template } from '@loomia/shared';

const QUICK_ACTIONS = [
  { tool: 'change_outfit', label: 'Change Outfit', icon: 'checkroom' },
  { tool: 'face_swap_image', label: 'Face Swap', icon: 'face_retouching_natural' },
  { tool: 'hair_salon', label: 'Hair Salon', icon: 'content_cut' },
  { tool: 'makeup_styles', label: 'Makeup Style', icon: 'brush' },
] as const;

/** Cards fetched per rail on All Effects (configurable). */
const RAIL_SIZE = 10;
/** Cards fetched per page in a category grid. */
const GRID_PAGE = 20;

// --- State that survives leaving and returning to Home -----------------------------

const memory = {
  tab: '', // '' = All Effects, otherwise a category slug
  scroll: new Map<string, number>(), // scrollY per tab
};

interface CategoryData {
  templates: Template[];
  nextCursor: string | null;
}
/** Per-category cache; rails and grids share the same list. */
const dataCache = new Map<string, CategoryData>();
let categoriesCache: Category[] | null = null;

/** Incremented on every render; async loaders bail out when stale. */
let renderSeq = 0;

function railSkeletonHtml(): string {
  return `<div class="rail" aria-label="Loading templates">${templateCardSkeletonHtml(5)}</div>`;
}

function gridSkeletonHtml(): string {
  return `<div class="tpl-grid" aria-label="Loading templates">${templateCardSkeletonHtml(6)}</div>`;
}

function errorHtml(action: string): string {
  return `
    <div class="empty">
      <div class="empty-icon" aria-hidden="true">${icon('cloud_off')}</div>
      <p class="muted" style="max-width: 260px;">Couldn't load templates. Check your connection and try again.</p>
      <button class="btn btn-secondary" data-action="${action}">Try again</button>
    </div>`;
}

function emptyHtml(): string {
  return `
    <div class="empty">
      <div class="empty-icon" aria-hidden="true">${icon('image')}</div>
      <p class="muted" style="max-width: 260px;">No templates in this category yet — check back soon.</p>
    </div>`;
}

function tabLabelHtml(slug: string, label: string): string {
  const name = slug === '' ? 'auto_awesome' : CATEGORY_ICONS[slug];
  return `${name ? icon(name, { fill: slug === '' }) : ''}<span>${escapeHtml(label)}</span>`;
}

export function renderHome(root: HTMLElement): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  root.innerHTML = `
    <div class="home">
      <a class="home-banner" href="#/explore" aria-label="All-in-one Creative Studio — Explore Now">
        <img src="/img/home/hero.webp" width="646" height="229" alt="" decoding="async" />
      </a>

      <div class="home-ctas">
        <a class="home-cta" href="#/editor/image-to-video" aria-label="Image To Video — turn photos into stunning videos">
          <img src="/img/home/tile-image-to-video.webp" width="333" height="195" alt="" decoding="async" />
        </a>
        <a class="home-cta" href="#/editor/edit-image" aria-label="Edit Image — transform your photos with AI">
          <img src="/img/home/tile-edit-image.webp" width="304" height="195" alt="" decoding="async" />
        </a>
      </div>

      <div class="quick-actions" aria-label="Quick actions">
        ${QUICK_ACTIONS.map(
          (a) => `
          <a class="quick-action" href="${a.tool === 'change_outfit' ? '#/category/change-outfit' : `#/editor/${a.tool}`}">
            ${icon(a.icon, { cls: 'quick-action-ic' })}
            <span>${a.label}</span>
          </a>`,
        ).join('')}
      </div>

      <div class="home-tabs-wrap">
        <div class="tabs home-tabs" id="home-tabs" role="tablist" aria-label="Categories"></div>
      </div>

      <div id="home-content" class="home-content" aria-live="polite"></div>
    </div>
  `;

  const tabsEl = root.querySelector<HTMLElement>('#home-tabs');
  const contentEl = root.querySelector<HTMLElement>('#home-content');
  if (!tabsEl || !contentEl) return;

  let categories: Category[] = categoriesCache ?? [];
  let selected = memory.tab;
  /** Aborts grid pagination observers when the tab/page changes. */
  let observer: IntersectionObserver | null = null;

  // Remember scroll position per tab while Home is on screen.
  let scrollRaf = 0;
  const onScroll = (): void => {
    if (!alive()) {
      window.removeEventListener('scroll', onScroll);
      return;
    }
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      memory.scroll.set(selected, window.scrollY);
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  // --- Tabs -----------------------------------------------------------------------

  const renderTabs = (): void => {
    const all = [{ slug: '', title: 'All Effects' }, ...categories.map((c) => ({ slug: c.slug, title: c.title }))];
    tabsEl.innerHTML = all
      .map(
        (t) => `
        <button class="tab${selected === t.slug ? ' active' : ''}" role="tab"
                aria-selected="${selected === t.slug}" data-cat="${escapeHtml(t.slug)}">
          ${tabLabelHtml(t.slug, t.title)}
        </button>`,
      )
      .join('');
  };

  const revealActiveTab = (): void => {
    const active = tabsEl.querySelector<HTMLElement>('.tab.active');
    if (!active) return;
    // Scroll only the tab strip (not the page) so the selected tab is visible.
    const left = active.offsetLeft - (tabsEl.clientWidth - active.offsetWidth) / 2;
    tabsEl.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  };

  // --- Data -----------------------------------------------------------------------

  const loadCategory = async (slug: string, limit: number, cursor?: string): Promise<CategoryData> => {
    const page = await api.listTemplates({
      category: slug,
      limit,
      ...(cursor ? { cursor } : {}),
    });
    return { templates: page.templates, nextCursor: page.nextCursor };
  };

  // --- All Effects: one rail per category -----------------------------------------

  const renderAllEffects = (): void => {
    const sections = categories
      .map(
        (c) => `
        <section class="home-section" id="sec-${escapeHtml(c.slug)}" data-slug="${escapeHtml(c.slug)}" aria-label="${escapeHtml(c.title)}">
          <div class="rail-head">
            <h2 class="section-title">${tabLabelHtml(c.slug, c.title)}</h2>
            <a class="see-all" href="#/home" data-see-all="${escapeHtml(c.slug)}">See all ${icon('arrow_forward')}</a>
          </div>
          <div class="section-body">${railSkeletonHtml()}</div>
        </section>`,
      )
      .join('');
    contentEl.innerHTML = sections;

    for (const c of categories) {
      void fillSection(c);
    }
  };

  const fillSection = async (c: Category): Promise<void> => {
    const sec = contentEl.querySelector<HTMLElement>(`.home-section[data-slug="${CSS.escape(c.slug)}"]`);
    const body = sec?.querySelector<HTMLElement>('.section-body');
    if (!sec || !body) return;
    try {
      let data = dataCache.get(c.slug);
      if (!data) {
        data = await loadCategory(c.slug, RAIL_SIZE);
        dataCache.set(c.slug, data);
      }
      if (!alive() || selected !== '') return;
      if (data.templates.length === 0) {
        sec.remove(); // hide categories that have nothing to show yet
        return;
      }
      body.innerHTML = `<div class="rail" role="list">${data.templates.slice(0, RAIL_SIZE).map((t) => templateCardHtml(t)).join('')}</div>`;
      bindCardImages(body);
      restoreScrollWhenReady();
    } catch (err) {
      if (!alive() || selected !== '') return;
      body.innerHTML = errorHtml('retry-section');
      body.dataset['slug'] = c.slug;
      toastApiError(err, `Couldn't load ${c.title}.`);
    }
  };

  // --- Category tab: 2-column grid with infinite loading --------------------------

  const renderGrid = async (slug: string): Promise<void> => {
    const cat = categories.find((c) => c.slug === slug);
    contentEl.innerHTML = `
      <section class="home-section" aria-label="${escapeHtml(cat?.title ?? 'Templates')}">
        <div class="tpl-grid-wrap">${gridSkeletonHtml()}</div>
      </section>`;
    const wrap = contentEl.querySelector<HTMLElement>('.tpl-grid-wrap');
    if (!wrap) return;

    let data = dataCache.get(`grid:${slug}`);
    try {
      if (!data) {
        data = await loadCategory(slug, GRID_PAGE);
        dataCache.set(`grid:${slug}`, data);
      }
    } catch (err) {
      if (!alive() || selected !== slug) return;
      wrap.innerHTML = errorHtml('retry-grid');
      toastApiError(err, "Couldn't load templates.");
      return;
    }
    if (!alive() || selected !== slug) return;
    if (data.templates.length === 0) {
      wrap.innerHTML = emptyHtml();
      return;
    }
    wrap.innerHTML = `<div class="tpl-grid" role="list">${data.templates.map((t) => templateCardHtml(t)).join('')}</div><div class="grid-sentinel" aria-hidden="true"></div>`;
    bindCardImages(wrap);
    restoreScrollWhenReady();
    observeSentinel(slug, wrap);
  };

  const observeSentinel = (slug: string, wrap: HTMLElement): void => {
    observer?.disconnect();
    const sentinel = wrap.querySelector<HTMLElement>('.grid-sentinel');
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    let busy = false;
    observer = new IntersectionObserver(
      (entries) => {
        if (busy || !entries.some((e) => e.isIntersecting)) return;
        const data = dataCache.get(`grid:${slug}`);
        if (!data?.nextCursor) {
          observer?.disconnect();
          return;
        }
        busy = true;
        void loadCategory(slug, GRID_PAGE, data.nextCursor)
          .then((next) => {
            if (!alive() || selected !== slug) return;
            const merged: CategoryData = {
              templates: [...data.templates, ...next.templates],
              nextCursor: next.nextCursor,
            };
            dataCache.set(`grid:${slug}`, merged);
            const grid = wrap.querySelector<HTMLElement>('.tpl-grid');
            if (grid) {
              const tmp = document.createElement('div');
              tmp.innerHTML = next.templates.map((t) => templateCardHtml(t)).join('');
              const added = Array.from(tmp.children) as HTMLElement[];
              added.forEach((el) => grid.appendChild(el));
              bindCardImages(grid);
            }
            if (!next.nextCursor) observer?.disconnect();
          })
          .catch((err) => toastApiError(err, "Couldn't load more templates."))
          .finally(() => {
            busy = false;
          });
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
  };

  // --- Scroll restore -------------------------------------------------------------

  let restorePending = true;
  function restoreScrollWhenReady(): void {
    if (!restorePending) return;
    restorePending = false;
    const y = memory.scroll.get(selected) ?? 0;
    requestAnimationFrame(() => window.scrollTo({ top: y }));
  }

  // --- Selecting a tab ------------------------------------------------------------

  const showTab = (slug: string, opts: { restore: boolean }): void => {
    observer?.disconnect();
    selected = slug;
    memory.tab = slug;
    restorePending = opts.restore;
    renderTabs();
    revealActiveTab();
    if (slug === '') renderAllEffects();
    else void renderGrid(slug);
    if (!opts.restore) {
      // Fresh selection: show the top of the content right under the sticky tabs.
      const top = contentEl.getBoundingClientRect().top + window.scrollY;
      const stickyOffset = (tabsEl.parentElement?.offsetHeight ?? 0) + topbarHeight();
      if (window.scrollY > top - stickyOffset) {
        window.scrollTo({ top: Math.max(0, top - stickyOffset) });
      }
    }
  };

  const topbarHeight = (): number => document.getElementById('topbar')?.offsetHeight ?? 0;

  tabsEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-cat]');
    if (!btn) return;
    const slug = btn.dataset['cat'] ?? '';
    if (slug === selected) return;
    memory.scroll.set(selected, window.scrollY);
    showTab(slug, { restore: false });
  });

  contentEl.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const seeAll = target.closest<HTMLElement>('[data-see-all]');
    if (seeAll) {
      e.preventDefault();
      showTab(seeAll.dataset['seeAll'] ?? '', { restore: false });
      return;
    }
    const retrySection = target.closest<HTMLElement>('[data-action="retry-section"]');
    if (retrySection) {
      const slug = retrySection.closest<HTMLElement>('.home-section')?.dataset['slug'];
      const cat = categories.find((c) => c.slug === slug);
      if (cat) {
        const body = retrySection.closest<HTMLElement>('.section-body');
        if (body) body.innerHTML = railSkeletonHtml();
        void fillSection(cat);
      }
      return;
    }
    if (target.closest('[data-action="retry-grid"]')) void renderGrid(selected);
  });

  // --- Initial load: categories first, then the selected tab ----------------------

  void (async () => {
    if (!categoriesCache) {
      try {
        const cats = await api.listCategories();
        categoriesCache = [...cats].sort((a, b) => a.sortOrder - b.sortOrder);
      } catch (err) {
        if (!alive()) return;
        toastApiError(err, "Couldn't load categories.");
        categoriesCache = [];
      }
    }
    if (!alive()) return;
    categories = categoriesCache;
    // A remembered tab may have been hidden in the CMS since last visit.
    if (selected !== '' && !categories.some((c) => c.slug === selected)) {
      selected = '';
      memory.tab = '';
    }
    showTab(selected, { restore: true });
  })();
}
